// @ts-strict-ignore
import {
    AllBetaTestingStyles,
    AppleAppStoreDefaultSettings,
    AppStoreType,
    BetaTestingStyle,
    GoogleAppStoreSettings,
    Integration,
    IntegrationCapability,
    IntegrationType,
    RegressionTestingStrategy,
    ReleaseMethod,
    ReleaseStepType,
} from '@/types';
import { FieldType, FormFieldData } from '@/helpers/formHelpers';
import { runwayColors } from '../styles/globalStyles';
import {
    BetaTestingStepConfig,
    BetaTestingStepSettings,
    BetaTestingStepSettingsRequestValues,
    InitialEncryptionOptions,
    MetadataStepConfig,
    MetadataStepSettingsRequestValues,
    RegressionTestingStepSettings,
    ReleaseStepConfig,
    ReleaseStepSettings,
    ReleaseStepSettingsRequestValues,
    RolloutStepConfig,
    RolloutStepSettings,
    RolloutStepSettingsRequestValues,
    ScreenshotsStepConfig,
    ScreenshotsStepSettingsRequestValues,
    StepConfig,
    StepConfigSharedSettings,
    StepConfigSummary,
    StepMetadataLocaleData,
    StoreReviewStepConfig,
    StoreReviewStepSettings,
    StoreReviewStepSettingsRequestValues,
    SubmissionStepConfig,
    SubmissionStepSettings,
    SubmissionStepSettingsRequestValues,
} from '@/types/stepConfig';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import Input from 'antd/lib/input';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import { dayjs } from '@/extendedDayjs';
import { allCases, Nullable } from '@/typescriptHelpers';
import Radio from 'antd/lib/radio';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { InfoCircleOutlined } from '@ant-design/icons';
import { convertAndroidStagedRolloutPercentage, displayTextForReleaseMethod } from '@/helpers/displayHelpers';
import { MetadataDefaultFields } from '../MetadataDefaultFields';
import { AndroidStagedRolloutPercentageInput } from '../sharedComponents/shared';

const { Text: TypographyText } = Typography;

export const getStepConfigSettingsInitialValuesByArchetype = <T extends ReleaseStepType>(
    stepConfig: StepConfig,
): StepConfigSettingsInitialValuesByArchetype<T> => {
    switch (stepConfig.archetype) {
        case ReleaseStepType.BetaTesting: {
            const initialSoakDuration =
                stepConfig?.settings?.prescribedBetaSoakDurationSeconds ?
                    dayjs.duration(stepConfig.settings.prescribedBetaSoakDurationSeconds, 'seconds').asHours()
                :   24;

            return {
                betaTestingStyle: stepConfig.settings.betaTestingStyle,
                prescribedBetaSoakDurationSeconds: initialSoakDuration,
                upstreamCIStepConfigId: stepConfig.settings.upstreamCIStepConfigId,
            } as StepConfigSettingsInitialValuesByArchetype<T>;
        }
        case ReleaseStepType.Submission:
        case ReleaseStepType.StoreReview:
        case ReleaseStepType.Takeoff:
            return {
                appStoreSettings:
                    stepConfig.settings.appStoreSettings ?
                        {
                            ...stepConfig.settings.appStoreSettings,
                            initialStagedRolloutPercentage:
                                'initialStagedRolloutPercentage' in stepConfig.settings.appStoreSettings ?
                                    convertAndroidStagedRolloutPercentage(
                                        stepConfig.settings.appStoreSettings.initialStagedRolloutPercentage,
                                    )
                                :   undefined,
                        }
                    :   null,
                encryptionSelection: stepConfig.settings.encryptionSelection,
                isAppAvailableInFrance: stepConfig.settings.isAppAvailableInFrance,
                upstreamCIStepConfigId: stepConfig.settings.upstreamCIStepConfigId,
            } as StepConfigSettingsInitialValuesByArchetype<T>;
        case ReleaseStepType.Metadata: {
            const metadataStepConfig = stepConfig as MetadataStepConfig;
            return {
                defaultMetadataByLocale: metadataStepConfig.settings.defaultMetadata.reduce(
                    (acc, locale) => {
                        acc[locale.locale] = {
                            whatsNew: locale.whatsNew,
                        };
                        return acc;
                    },
                    {} as Record<string, StepMetadataLocaleData>,
                ),
                upstreamBetaStepConfigId: metadataStepConfig.settings.upstreamBetaStepConfigId,
                requiresManualApproval: metadataStepConfig.settings?.requiresManualApproval ?? true,
            } as StepConfigSettingsInitialValuesByArchetype<T>;
        }
        case ReleaseStepType.Screenshots: {
            const screenshotsStepConfig = stepConfig as ScreenshotsStepConfig;
            return {
                requiresManualApproval: screenshotsStepConfig.settings?.requiresManualApproval ?? true,
            } as StepConfigSettingsInitialValuesByArchetype<T>;
        }
        case ReleaseStepType.Rollout: {
            const rolloutStepConfig = stepConfig as RolloutStepConfig;
            return {
                sourceStepConfigId: rolloutStepConfig.settings.sourceStepConfigId,
            } as StepConfigSettingsInitialValuesByArchetype<T>;
        }
        // Add other archetypes as needed
        default:
            return {} as StepConfigSettingsInitialValuesByArchetype<T>;
    }
};

export const getIntegrationSettingsByArchetype = <T extends ReleaseStepType>(
    stepConfig: StepConfig,
): IntegrationSettingsByArchetype<T> => {
    switch (stepConfig.archetype) {
        case ReleaseStepType.BetaTesting:
            return {
                googleTestingTrackName: stepConfig.settings.googleTestingTrackName,
            } as IntegrationSettingsByArchetype<T>;
        default:
            return {} as IntegrationSettingsByArchetype<T>;
    }
};

export type IntegrationSettingsByArchetype<T extends ReleaseStepType> =
    T extends ReleaseStepType.BetaTesting ? Pick<BetaTestingStepSettings, 'googleTestingTrackName'> : never;

export type BetaTestingStepSettingsInitialValues = Omit<
    BetaTestingStepSettings,
    'googleTestingTrackName' | 'upstreamCIStepConfigOptions'
>;
export interface MetadataStepSettingsInitialValues extends MetadataStepSettingsRequestValues {} // this is a requestvalues interface on purpose, since the initial values are transformed to request values
export interface StoreReviewStepSettingsInitialValues
    extends Omit<StoreReviewStepSettings, 'upstreamCIStepConfigOptions'> {}
export interface SubmissionStepSettingsInitialValues
    extends Omit<SubmissionStepSettings, 'upstreamCIStepConfigOptions'> {}
export interface ReleaseStepSettingsInitialValues extends Omit<ReleaseStepSettings, 'upstreamCIStepConfigOptions'> {}
export interface RolloutStepSettingsInitialValues extends Omit<RolloutStepSettings, 'sourceStepConfigOptions'> {}
export interface RegressionTestingStepSettingsInitialValues extends RegressionTestingStepSettings {}
export interface ScreenshotsStepSettingsInitialValues extends ScreenshotsStepSettingsRequestValues {}

export type StepConfigSettingsInitialValuesByArchetype<T extends ReleaseStepType> =
    T extends ReleaseStepType.BetaTesting ? BetaTestingStepSettingsInitialValues
    : T extends ReleaseStepType.Metadata ? MetadataStepSettingsInitialValues
    : T extends ReleaseStepType.Rollout ? RolloutStepSettingsInitialValues
    : T extends ReleaseStepType.Screenshots ? ScreenshotsStepSettingsInitialValues
    : T extends ReleaseStepType.StoreReview ? StoreReviewStepSettingsInitialValues
    : T extends ReleaseStepType.Takeoff ? ReleaseStepSettingsInitialValues
    : T extends ReleaseStepType.Submission ? SubmissionStepSettingsInitialValues
    : T extends ReleaseStepType.RegressionTesting ? RegressionTestingStepSettingsInitialValues
    : never;

export type StepConfigSettingsRequestValuesByArchetype<T extends ReleaseStepType> =
    T extends ReleaseStepType.BetaTesting ? BetaTestingStepSettingsRequestValues
    : T extends ReleaseStepType.Submission ? SubmissionStepSettingsRequestValues
    : T extends ReleaseStepType.StoreReview ? StoreReviewStepSettingsRequestValues
    : T extends ReleaseStepType.Takeoff ? ReleaseStepSettingsRequestValues
    : T extends ReleaseStepType.Metadata ? MetadataStepSettingsRequestValues
    : T extends ReleaseStepType.Rollout ? RolloutStepSettingsRequestValues
    : T extends ReleaseStepType.Screenshots ? ScreenshotsStepSettingsRequestValues
    : T extends ReleaseStepType.RegressionTesting ? RegressionTestingStepSettings
    : never;

interface StepConfigSettingsFormFieldsParams {
    stepConfig: StepConfig;
    isEditable?: boolean;
    shouldAddDefaultWarningText?: boolean;
}

interface IntegrationSpecificSettingsFormFieldsParams {
    archetype: ReleaseStepType;
    allConnectedIntegrations: Integration[];
    isEditable?: boolean;
}

export const betaStyleString = (style: BetaTestingStyle) => {
    switch (style) {
        case BetaTestingStyle.NonBlocking:
            return 'non-blocking';
        case BetaTestingStyle.OpenEndedSoak:
            return 'open-ended soak';
        case BetaTestingStyle.TimeLimitedSoak:
            return 'time-limited soak';
    }
};

const descriptionForStyle = (style: BetaTestingStyle) => {
    switch (style) {
        case BetaTestingStyle.NonBlocking:
            return 'No soak period. Step will automatically be set to a green status.';
        case BetaTestingStyle.OpenEndedSoak:
            return 'Open-ended beta testing soak period. No minimum soak duration is required.';
        case BetaTestingStyle.TimeLimitedSoak:
            return 'The beta build must soak for the given amount of time.';
    }
};

export const regressionTestingStrategyString = (strategy: RegressionTestingStrategy) => {
    switch (strategy) {
        case RegressionTestingStrategy.ClearStatusOnNewBuild:
            return 'If Runway detects a new build, clear current build and status';
        case RegressionTestingStrategy.MarkLatestRCAsInProgress:
            return 'If Runway detects a new build, update selected build and reset status to "In progress"';
        case RegressionTestingStrategy.ManualUpdatesOnly:
            return 'Do not automatically update selected build or status, manual updates only';
        case RegressionTestingStrategy.StatusBasedOnItems:
            return 'Status of regression testing items determines build testing status';
        case RegressionTestingStrategy.StatusBasedOnItemsWithCLIs:
            return 'Status of regression testing items and checklist items determines build testing status';
        case RegressionTestingStrategy.DisableOverallStatus:
            return 'Completion of regression testing items always updates build testing status to Passed';
        case RegressionTestingStrategy.DisableOverallStatusWithCLIs:
            return 'Completion of regression testing items and checklist items always updates build testing status to Passed';
    }
};

export const descriptionForRegressionTestingStrategy = (strategy?: RegressionTestingStrategy) => {
    switch (strategy) {
        case RegressionTestingStrategy.ClearStatusOnNewBuild:
            return 'This option requires a build and status to be manually selected in the Build testing status dropdown when testing.';
        default:
            return null;
    }
};

export const transformStepConfigSettingsToUpdateRequestValues = <T extends ReleaseStepType>(
    archetype: T,
    values: StepConfigSettingsInitialValuesByArchetype<T>,
    branchConfigId: Nullable<string>,
    tagConfigId: Nullable<string>,
    featureAffiliationConfigId: Nullable<string>,
): StepConfigSettingsRequestValuesByArchetype<T> => {
    const sharedSettings: StepConfigSharedSettings = {
        branchConfigId: branchConfigId ?? undefined,
        tagConfigId: tagConfigId ?? undefined,
        featureAffiliationConfigId: featureAffiliationConfigId ?? undefined,
    };

    switch (archetype) {
        case ReleaseStepType.BetaTesting: {
            const betaValues = values as Partial<BetaTestingStepSettings>;
            const betaSettings: Partial<BetaTestingStepSettingsRequestValues> = {
                betaTestingStyle: betaValues?.betaTestingStyle ?? undefined,
                betaSoakDurationSeconds:
                    betaValues?.prescribedBetaSoakDurationSeconds ?
                        dayjs.duration(Number(betaValues.prescribedBetaSoakDurationSeconds), 'hours').asSeconds()
                    :   undefined,
                testingTrack: betaValues?.googleTestingTrackName ?? undefined,
                upstreamCIStepConfigId: betaValues?.upstreamCIStepConfigId ?? undefined,
                ...sharedSettings,
            };
            return betaSettings as StepConfigSettingsRequestValuesByArchetype<T>;
        }
        case ReleaseStepType.RegressionTesting: {
            const regressionValues = values as Partial<RegressionTestingStepSettings>;
            const regressionSettings: Partial<RegressionTestingStepSettings> = {
                regressionTestingStrategy:
                    regressionValues?.regressionTestingStrategy ?? RegressionTestingStrategy.ManualUpdatesOnly,
                ...sharedSettings,
            };
            return regressionSettings as StepConfigSettingsRequestValuesByArchetype<T>;
        }
        case ReleaseStepType.StoreReview:
        case ReleaseStepType.Takeoff:
        case ReleaseStepType.Submission: {
            const releaseStepValues = (values ?? {}) as Partial<ReleaseStepSettingsInitialValues>;
            const appStoreSettings = releaseStepValues.appStoreSettings;

            if (appStoreSettings && 'initialStagedRolloutPercentage' in appStoreSettings) {
                appStoreSettings.rolloutPercentage = {
                    fraction:
                        appStoreSettings.initialStagedRolloutPercentage ?
                            appStoreSettings.initialStagedRolloutPercentage / 100
                        :   0,
                };
            }

            const releaseStepSettings: ReleaseStepSettingsInitialValues = {
                appStoreSettings,
                encryptionSelection: releaseStepValues.encryptionSelection,
                isAppAvailableInFrance: releaseStepValues.isAppAvailableInFrance,
                upstreamCIStepConfigId: releaseStepValues.upstreamCIStepConfigId,
                ...sharedSettings,
            };
            return releaseStepSettings as StepConfigSettingsRequestValuesByArchetype<T>;
        }
        case ReleaseStepType.Metadata: {
            const metadataValues = values as MetadataStepSettingsInitialValues;
            const metadataSettings: MetadataStepSettingsRequestValues = {
                ...metadataValues,
                ...sharedSettings,
            };
            return metadataSettings as StepConfigSettingsRequestValuesByArchetype<T>;
        }
        case ReleaseStepType.Rollout: {
            const rolloutValues = values as RolloutStepSettingsInitialValues;
            const rolloutSettings: RolloutStepSettingsRequestValues = {
                sourceStepConfigId: rolloutValues?.sourceStepConfigId ?? undefined,
                ...sharedSettings,
            };
            return rolloutSettings as StepConfigSettingsRequestValuesByArchetype<T>;
        }
        case ReleaseStepType.Screenshots: {
            const screenshotsValues = (values ?? {}) as Partial<ScreenshotsStepSettingsRequestValues>;
            const screenshotsSettings: ScreenshotsStepSettingsRequestValues = {
                requiresManualApproval: screenshotsValues.requiresManualApproval ?? true,
                ...sharedSettings,
            };
            return screenshotsSettings as StepConfigSettingsRequestValuesByArchetype<T>;
        }
        default:
            return sharedSettings as StepConfigSettingsRequestValuesByArchetype<T>;
    }
};

export const getBetaTestingStepFields = ({ isEditable = true }): FormFieldData[] => {
    return [
        {
            fieldType: FieldType.Custom,
            required: true,
            renderField: () => (
                <Form.Item shouldUpdate noStyle>
                    {(form) => {
                        const selectedBetaStyle = form.getFieldValue('betaTestingStyle');
                        return (
                            <Form.Item
                                label="Beta testing style"
                                name="betaTestingStyle"
                                help={descriptionForStyle(selectedBetaStyle)}
                                style={{ margin: 0 }}
                                required
                            >
                                <Select disabled={!isEditable}>
                                    {AllBetaTestingStyles.map((betaStyle) => {
                                        const optionText = betaStyleString(betaStyle);
                                        return (
                                            <Select.Option key={betaStyle} value={betaStyle}>
                                                {optionText}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            ),
        },
        {
            fieldType: FieldType.Custom,
            renderField: () => (
                <Form.Item shouldUpdate noStyle>
                    {(form) => {
                        const selectedBetaStyle = form.getFieldValue('betaTestingStyle');
                        return (
                            selectedBetaStyle === BetaTestingStyle.TimeLimitedSoak && (
                                <Form.Item
                                    label="Soak duration"
                                    name="prescribedBetaSoakDurationSeconds"
                                    style={{ margin: 0 }}
                                    rules={[
                                        { required: true },
                                        {
                                            pattern: /^\d+$/,
                                            message: 'The soak duration must be a whole number',
                                        },
                                        {
                                            validator: (rule, value) => {
                                                if (value > 0) {
                                                    return Promise.resolve();
                                                } else {
                                                    return Promise.reject(
                                                        'The soak duration period must be a value greater than zero',
                                                    );
                                                }
                                            },
                                        },
                                    ]}
                                    extra={<Text type="secondary">The soak duration is expressed in hours</Text>}
                                >
                                    <Input type="number" autoComplete="off" disabled={!isEditable} />
                                </Form.Item>
                            )
                        );
                    }}
                </Form.Item>
            ),
        },
    ];
};

export const getRegressionTestingStepFields = (isWorkflow = false): FormFieldData[] => {
    const workflowOnlyStrategies: RegressionTestingStrategy[] = [
        RegressionTestingStrategy.StatusBasedOnItems,
        RegressionTestingStrategy.StatusBasedOnItemsWithCLIs,
        RegressionTestingStrategy.DisableOverallStatusWithCLIs,
    ];
    const strategies = Object.values(RegressionTestingStrategy).filter(
        (strategy) => isWorkflow || !workflowOnlyStrategies.includes(strategy),
    );
    const data = strategies.map((strategy) => ({
        value: strategy,
        displayValue: regressionTestingStrategyString(strategy),
    }));
    return [
        {
            fieldType: FieldType.Select,
            required: true,
            data,
            label: 'Build testing status handling',
            name: 'regressionTestingStrategy',
            extraFormItemProps: {
                layout: 'vertical',
            },
        },
    ];
};

export const getStepConfigSettingsFields = ({
    isEditable = true,
    stepConfig,
    shouldAddDefaultWarningText: shouldAddDefaultWarningTextProp = true,
}: StepConfigSettingsFormFieldsParams): FormFieldData[] => {
    let stepConfigSettingsFields: FormFieldData[] = [];
    let shouldAddDefaultWarningText = shouldAddDefaultWarningTextProp;
    const upstreamCIStepConfigHelp =
        'The upstream CI step config is the step config that will be used to get the build from.';

    const archetype = stepConfig.archetype;
    const destinations = stepConfig.destinations;
    const firstDestination = destinations[0];

    const requiresManualApprovalField = (initialValue: boolean): FormFieldData => ({
        fieldType: FieldType.Switch,
        name: 'requiresManualApproval',
        label: 'Require manual approval',
        disabled: !isEditable,
        initialValue,
    });

    switch (archetype) {
        case ReleaseStepType.Metadata: {
            const metadataStepConfig = stepConfig as MetadataStepConfig;
            shouldAddDefaultWarningText = false;
            stepConfigSettingsFields = [
                requiresManualApprovalField(metadataStepConfig.settings?.requiresManualApproval ?? true),
                {
                    fieldType: FieldType.Custom,
                    skipFormItemWrapper: true,
                    renderField: () => (
                        <MetadataDefaultFields
                            appStoreType={firstDestination.appStoreType}
                            defaultMetadata={metadataStepConfig.settings.defaultMetadata}
                        />
                    ),
                },
                {
                    fieldType: FieldType.Select,
                    name: 'upstreamBetaStepConfigId',
                    label: 'Upstream beta step config',
                    helpMessage:
                        'The upstream beta step config is the step config that will be used to get beta tester notes from.',
                    disabled: !isEditable,
                    data: Object.entries(metadataStepConfig.settings.upstreamBetaStepConfigOptions).map(
                        ([key, value]) => {
                            return {
                                value: key,
                                displayValue: value.name,
                            };
                        },
                    ),
                },
            ];
            break;
        }
        case ReleaseStepType.Screenshots: {
            const screenshotsStepConfig = stepConfig as ScreenshotsStepConfig;
            stepConfigSettingsFields = [
                requiresManualApprovalField(screenshotsStepConfig.settings?.requiresManualApproval ?? true),
            ];
            break;
        }
        case ReleaseStepType.BetaTesting: {
            stepConfigSettingsFields = getBetaTestingStepFields({ isEditable });

            const betaStepConfig = stepConfig as BetaTestingStepConfig;
            const upstreamCIOptions = Object.entries(betaStepConfig.settings.upstreamCIStepConfigOptions || {}).map(
                ([key, value]: [string, StepConfigSummary]) => ({
                    value: key,
                    displayValue: value.name,
                }),
            );

            stepConfigSettingsFields.push({
                fieldType: FieldType.Select,
                name: 'upstreamCIStepConfigId',
                label: 'Upstream CI step config',
                helpMessage: upstreamCIStepConfigHelp,
                disabled: !isEditable,
                data: upstreamCIOptions,
            });

            break;
        }
        case ReleaseStepType.Submission:
        case ReleaseStepType.StoreReview:
        case ReleaseStepType.Takeoff: {
            stepConfigSettingsFields = [];
            // Show Apple-specific fields if appleAppStoreSettings is present
            if (firstDestination.appStoreType === AppStoreType.Apple) {
                const releaseMethod = [`appStoreSettings`, 'releaseMethod'];
                const phasedReleaseEnabledPath = [`appStoreSettings`, 'phasedRelease'];
                const shouldApplyDefaultEncryptionPath = [`appStoreSettings`, 'shouldApplyDefaultEncryption'];

                stepConfigSettingsFields.push({
                    fieldType: FieldType.Select,
                    name: releaseMethod,
                    label: 'Release type',
                    disabled: !isEditable,
                    data: allCases(ReleaseMethod).map((releaseMethod) => {
                        return { value: releaseMethod, displayValue: displayTextForReleaseMethod(releaseMethod) };
                    }),
                });

                // Show phased release field (always available for Apple settings)
                const appStoreSettings = stepConfig.settings.appStoreSettings as AppleAppStoreDefaultSettings;
                stepConfigSettingsFields.push({
                    fieldType: FieldType.Checkbox,
                    name: phasedReleaseEnabledPath,
                    data: 'Phased release enabled',
                    helpMessage:
                        'Releases this update in stages over a 7-day period to users who have turned on automatic updates. (1% of users on Day 1, 2% on Day 2, 5% on Day 3, 10% on Day 4, 20% on Day 5, 50% on Day 6, and 100% on Day 7). This version will still be available to all users as a manual update from the App Store.',
                    disabled: !isEditable,
                    initialValue: appStoreSettings?.phasedRelease,
                });

                // Export Compliance Information section
                stepConfigSettingsFields.push({
                    fieldType: FieldType.Custom,
                    skipFormItemWrapper: true,
                    renderField: () => (
                        <Form.Item
                            label={
                                <Space>
                                    <TypographyText>
                                        What type of encryption algorithms does your app implement?
                                    </TypographyText>
                                    <Tooltip title="To bypass setting up export compliance in App Store Connect, you can specify your use of encryption directly in the information property list (Info.plist) in your Xcode project. If you need to provide documentation, Apple will provide you with a key value to add to the Info.plist.">
                                        <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                                    </Tooltip>
                                </Space>
                            }
                            name={'encryptionSelection'}
                        >
                            <Radio.Group disabled={!isEditable}>
                                <Space direction="vertical" size={12}>
                                    <Radio value={InitialEncryptionOptions.ProprietaryAlgorithms}>
                                        Encryption algorithms that are proprietary or not accepted as standard by
                                        international standard bodies (IEEE, IETF, ITU, etc.)
                                    </Radio>
                                    <Radio value={InitialEncryptionOptions.StandardAlgorithms}>
                                        Standard encryption algorithms instead of, or in addition to, using or accessing
                                        the encryption within Apple's operating system
                                    </Radio>
                                    <Radio value={InitialEncryptionOptions.BothAlgorithms}>
                                        Both algorithms mentioned above
                                    </Radio>
                                    <Radio value={InitialEncryptionOptions.NoAlgorithms}>
                                        None of the algorithms mentioned above
                                    </Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                    ),
                });

                // France availability question (conditional)
                stepConfigSettingsFields.push({
                    fieldType: FieldType.Custom,
                    skipFormItemWrapper: true,
                    renderField: () => (
                        <Form.Item shouldUpdate noStyle>
                            {(form) => {
                                // We need to track the encryption selection type, not just the boolean result
                                // The France question should only show for StandardAlgorithms or BothAlgorithms
                                const encryptionSelection = form.getFieldValue(['encryptionSelection']);
                                const shouldShowFranceQuestion =
                                    encryptionSelection === InitialEncryptionOptions.StandardAlgorithms ||
                                    encryptionSelection === InitialEncryptionOptions.BothAlgorithms;

                                return (
                                    shouldShowFranceQuestion && (
                                        <Form.Item
                                            label={
                                                <TypographyText>
                                                    Is your app going to be available on the App Store in France?
                                                </TypographyText>
                                            }
                                            name={'isAppAvailableInFrance'}
                                        >
                                            <Radio.Group disabled={!isEditable}>
                                                <Radio value={true}>Yes</Radio>
                                                <Radio value={false}>No</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    )
                                );
                            }}
                        </Form.Item>
                    ),
                });

                stepConfigSettingsFields.push({
                    data: 'Apply default encryption settings to new builds',
                    fieldType: FieldType.Checkbox,
                    helpMessage:
                        'Runway will apply your default Export Compliance Information answers as defined here to new builds as they are uploaded to App Store Connect and TestFlight if needed.',
                    name: shouldApplyDefaultEncryptionPath,
                    disabled: !isEditable,
                    initialValue: appStoreSettings?.shouldApplyDefaultEncryption,
                });
            }

            // Show Google/Huawei-specific fields if googleAndHuaweiAppStoreSettings is present
            if (
                firstDestination.appStoreType === AppStoreType.Google ||
                firstDestination.appStoreType === AppStoreType.Huawei
            ) {
                const stagedReleasePath = [`appStoreSettings`, 'stagedRelease'];
                const initialStagedRolloutPercentagePath = [`appStoreSettings`, 'initialStagedRolloutPercentage'];

                // Show staged release field (always available for Google/Huawei settings)
                const appStoreSettings = stepConfig.settings.appStoreSettings as GoogleAppStoreSettings;
                stepConfigSettingsFields.push({
                    fieldType: FieldType.Checkbox,
                    name: stagedReleasePath,
                    data: 'Staged release',
                    disabled: !isEditable,
                    initialValue: appStoreSettings?.stagedRelease,
                    helpMessage:
                        'Enable staged release to gradually roll out your app to a percentage of users before making it available to everyone.',
                });

                // Only show initial staged rollout percentage if it's not null
                const stagedReleaseLabel =
                    firstDestination.appStoreType === AppStoreType.Huawei ?
                        'Initial phased release percentage'
                    :   'Initial staged rollout percentage';

                stepConfigSettingsFields.push({
                    fieldType: FieldType.Custom,
                    skipFormItemWrapper: true,
                    renderField: () => (
                        <Form.Item shouldUpdate noStyle>
                            {(form) => {
                                const stagedReleaseEnabled = form.getFieldValue(stagedReleasePath);
                                return (
                                    stagedReleaseEnabled && (
                                        <Form.Item
                                            label={stagedReleaseLabel}
                                            name={initialStagedRolloutPercentagePath}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Please enter an initial rollout percentage',
                                                },
                                                {
                                                    type: 'number',
                                                    min: 1,
                                                    max: 100,
                                                    message: 'Percentage must be between 1 and 100',
                                                },
                                            ]}
                                            extra={
                                                <TypographyText type="secondary">
                                                    Percentage of users who will initially receive the staged release
                                                    (1-100%)
                                                </TypographyText>
                                            }
                                        >
                                            <AndroidStagedRolloutPercentageInput
                                                style={{ width: '100%', maxWidth: 200 }}
                                                placeholder="Enter percentage"
                                                disabled={!isEditable}
                                            />
                                        </Form.Item>
                                    )
                                );
                            }}
                        </Form.Item>
                    ),
                });
            }

            const releaseStepConfigTyped = stepConfig as
                | ReleaseStepConfig
                | SubmissionStepConfig
                | StoreReviewStepConfig;
            const upstreamCIOptionsForRelease = Object.entries(
                releaseStepConfigTyped.settings.upstreamCIStepConfigOptions || {},
            ).map(([key, value]: [string, StepConfigSummary]) => ({
                value: key,
                displayValue: value.name,
            }));

            stepConfigSettingsFields.push({
                fieldType: FieldType.Select,
                name: 'upstreamCIStepConfigId',
                label: 'Upstream CI step config',
                helpMessage: upstreamCIStepConfigHelp,
                disabled: !isEditable,
                data: upstreamCIOptionsForRelease,
            });
            break;
        }
        case ReleaseStepType.Rollout: {
            const rolloutStepConfig = stepConfig as RolloutStepConfig;
            const sourceStepConfigOptions = Object.entries(
                rolloutStepConfig.settings.sourceStepConfigOptions || {},
            ).map(([key, value]: [string, StepConfigSummary]) => ({
                value: key,
                displayValue: value.name,
            }));

            stepConfigSettingsFields = [
                {
                    fieldType: FieldType.Select,
                    name: 'sourceStepConfigId',
                    label: 'Source step',
                    helpMessage:
                        'Either a Release or Beta step used as the rollout source to determine the builds for which to fetch rollout data.',
                    disabled: !isEditable,
                    data: sourceStepConfigOptions,
                },
            ];
            break;
        }
        default:
            return [];
    }

    if (shouldAddDefaultWarningText) {
        // add default warning text
        stepConfigSettingsFields.push({
            fieldType: FieldType.Custom,
            skipFormItemWrapper: true,
            renderField: () => (
                <div style={{ marginTop: 0, paddingTop: 26 }}>
                    <Text type="secondary">Your settings will apply to all active releases for this app.</Text>
                </div>
            ),
        });
    }
    return stepConfigSettingsFields;
};

export const getIntegrationSpecificSettingsFields = ({
    archetype,
    allConnectedIntegrations,
    isEditable = true,
}: IntegrationSpecificSettingsFormFieldsParams): Record<string, FormFieldData[]> => {
    let integrationSpecificSettingsFields: Record<string, FormFieldData[]> = {};

    switch (archetype) {
        case ReleaseStepType.BetaTesting:
            for (const integration of allConnectedIntegrations) {
                const hasGoogleBetaIntegration =
                    integration.id === IntegrationType.GoogleBeta &&
                    integration.capabilities.includes(IntegrationCapability.BetaTesting);

                if (hasGoogleBetaIntegration) {
                    // Add the google testing track name field to the integration specific settings fields
                    integrationSpecificSettingsFields[integration.installationId] = [
                        {
                            name: 'googleTestingTrackName',
                            label: 'Testing track name',
                            fieldType: FieldType.Input,
                            disabled: !isEditable,
                            rules: [{ required: true, message: 'Please enter a testing track name' }],
                        },
                    ];
                }
            }
            break;
        default:
            return {};
    }

    // add default prefix "integrationSpecificSettings" to the name of the fields
    for (const installationId in integrationSpecificSettingsFields) {
        integrationSpecificSettingsFields[installationId] = integrationSpecificSettingsFields[installationId].map(
            (field) => {
                return {
                    ...field,
                    name: ['integrationSpecificSettings', field.name],
                };
            },
        );
    }

    return integrationSpecificSettingsFields;
};
